import classnames from 'classnames';
import React from 'react';

import Icon from 'dpl/common/components/Icon';
import useFormContext from 'dpl/shared/form/hooks/useFormContext';
import useToggle from 'dpl/shared/hooks/useToggle';
import StripeChargeModalPaymentSummaryV4 from 'dpl/components/StripeCharge/New/StripeChargeModal/StripeChargeModalPaymentSummaryV4';

type PaymentSummaryProps = React.ComponentProps<
  typeof StripeChargeModalPaymentSummaryV4
>;

interface SummaryV3Props {
  title?: string | null;
  paymentRequest: {
    total_in_dollars?: string;
    fee_bundle?: PaymentSummaryProps['feeBundle'];
  };
  isLoadingSummary?: boolean;
  isTaxPending?: boolean;
  handleChangeTierSelection: NonNullable<
    PaymentSummaryProps['onChangeSelection']
  >;
  lineItems: PaymentSummaryProps['lineItems'];
  taxAmount: PaymentSummaryProps['taxAmount'];
  taxBreakdown?: PaymentSummaryProps['taxBreakdown'];
  depositRefundPolicy?: string | null;
  depositSecures?: string | null;
  finalPaymentTiming?: string | null;
}

// "Total due" with the breakdown behind it. While an update is out the
// amounts dim but stay tappable, so a tap mid-update isn't lost.
export default function SummaryV3({
  title = null,
  paymentRequest,
  isLoadingSummary = false,
  isTaxPending = false,
  handleChangeTierSelection,
  lineItems,
  taxAmount,
  taxBreakdown = null,
  depositRefundPolicy = null,
  depositSecures = null,
  finalPaymentTiming = null
}: SummaryV3Props) {
  const { formState } = useFormContext();
  const [isExpanded, toggleIsExpanded] = useToggle(false);

  return (
    <div data-test-id="price-details">
      {title && <h2 className="font-serif font-18 lh-title mb3">{title}</h2>}
      <button
        type="button"
        className={classnames('flex items-center w-100', {
          'pb3 bb b--light-gray': isExpanded
        })}
        onClick={() => toggleIsExpanded()}
        aria-expanded={isExpanded}
        aria-busy={isLoadingSummary}
      >
        <span className="font-16 fw-medium flex-auto tl">Total due</span>
        <span
          className={classnames('font-serif font-20', {
            'o-50': isLoadingSummary
          })}
          data-test-id="price-details-total"
        >
          {paymentRequest.total_in_dollars}
        </span>
        <Icon
          className="ml2"
          name={isExpanded ? 'fetch-chevron-up' : 'fetch-chevron-down'}
          width="16px"
          height="16px"
        />
      </button>
      {isExpanded && (
        <StripeChargeModalPaymentSummaryV4
          className={classnames({ 'o-50': isLoadingSummary })}
          amountSubtotal={formState.amount_in_dollars}
          taxAmount={taxAmount}
          taxBreakdown={taxBreakdown}
          isTaxPending={isTaxPending}
          lineItems={lineItems}
          paymentTier={formState.payment_tier}
          paymentMethod={formState.payment_method}
          category={formState.category}
          onChangeSelection={handleChangeTierSelection}
          feeBundle={paymentRequest.fee_bundle}
          depositRefundPolicy={depositRefundPolicy}
          depositSecures={depositSecures}
          finalPaymentTiming={finalPaymentTiming}
        />
      )}
    </div>
  );
}
